import { articles, categoryNav, type ArticleItem } from "@/mocks/chigua";
import NewsCard from "./NewsCard";
import RankingRail from "./RankingRail";

interface FeedProps {
  category: string;
  query: string;
  setCategory: (id: string) => void;
  onKeyword: (k: string) => void;
}

function matchQuery(item: ArticleItem, query: string) {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    item.title.toLowerCase().includes(q) ||
    item.excerpt.toLowerCase().includes(q) ||
    item.tag.toLowerCase().includes(q) ||
    item.category.includes(q)
  );
}

export default function Feed({ category, query, setCategory, onKeyword }: FeedProps) {
  const tabs = [{ id: "all", name: "全部" }, ...categoryNav];

  const filtered = articles.filter((a) => {
    const catOk = category === "all" || a.category === category;
    return catOk && matchQuery(a, query);
  });

  const activeName = tabs.find((t) => t.id === category)?.name ?? "全部";

  const scrollToTop = () => {
    document.getElementById("feed")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="feed" className="scroll-mt-24">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid grid-cols-1 gap-7 lg:grid-cols-[1fr_320px]">
          {/* 主信息流 */}
          <div>
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <p className="inline-flex items-center gap-2 font-label text-xs font-bold uppercase tracking-widest text-primary-600">
                  <i className="ri-fire-fill text-accent-500"></i>
                  Latest News
                </p>
                <h2 className="rule-head mt-2 font-heading text-2xl font-black text-foreground-950">
                  最新资讯动态
                </h2>
              </div>
              <span className="inline-flex items-center gap-1.5 text-sm text-foreground-500">
                <i className="ri-article-line"></i>
                已收录 {articles.length} 篇头条
              </span>
            </div>

            {/* 栏目筛选 */}
            <div className="editor-scroll mt-5 flex gap-2 overflow-x-auto pb-1">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => {
                    setCategory(t.id);
                    scrollToTop();
                  }}
                  className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-1.5 font-label text-sm transition ${
                    category === t.id
                      ? "bg-foreground-950 font-bold text-background-50"
                      : "bg-background-100 font-medium text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  {t.id !== "all" && <i className={`${t.icon} text-base`}></i>}
                  {t.name}
                </button>
              ))}
            </div>

            {/* 搜索结果提示 */}
            {query.trim() && (
              <div className="mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-accent-200 bg-accent-50 px-4 py-2.5 text-sm text-accent-900">
                <i className="ri-search-eye-line text-accent-700"></i>
                正在检索「{query}」，共找到 {filtered.length} 条相关资讯
                <button
                  type="button"
                  onClick={() => onKeyword("")}
                  className="ml-auto inline-flex items-center gap-1 font-medium text-accent-700 underline-offset-2 hover:underline"
                >
                  清除筛选
                  <i className="ri-close-circle-fill"></i>
                </button>
              </div>
            )}

            {/* 文章网格 */}
            {filtered.length > 0 ? (
              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
                {filtered.map((a) => (
                  <NewsCard key={a.id} item={a} />
                ))}
              </div>
            ) : (
              <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
                <i className="ri-folder-forbid-fill text-4xl text-foreground-300"></i>
                <p className="mt-4 font-heading text-lg font-bold text-foreground-900">
                  「{activeName}」暂时没有匹配的资讯
                </p>
                <p className="mt-2 max-w-sm text-sm text-foreground-500">
                  换个关键词，或切换栏目再看看。也可以从热搜热词中寻找灵感。
                </p>
                <button
                  type="button"
                  onClick={() => onKeyword("")}
                  className="mt-5 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 font-label text-sm font-bold text-background-50 transition hover:bg-primary-600"
                >
                  查看全部资讯
                </button>
              </div>
            )}
          </div>

          {/* 右栏 */}
          <RankingRail onKeyword={onKeyword} />
        </div>
      </div>
    </section>
  );
}